import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import type { WalletEntry } from "./wallet";

/**
 * SMS Gateway and SMS fund (owner 2026-10-30; radaccess handler/smsgateway.go).
 *
 *   GET  /operator/{ns}/smsgateway                                  accounts + the choice
 *   POST /operator/{ns}/smsgateway/accounts                         add (authkey goes in, never out)
 *   PUT  /operator/{ns}/smsgateway/accounts/{id}                    change
 *   POST /operator/{ns}/smsgateway/accounts/{id}/verify             one test SMS to the caller's own mobile
 *   GET  /operator/{ns}/smsgateway/accounts/{id}/templates          messages + this account's template ids
 *   PUT  /operator/{ns}/smsgateway/accounts/{id}/templates/{type}   map one ("" removes)
 *   PUT  /operator/{ns}/smsgateway/setting                          the choice
 *   GET  /operator/{ns}/smsfund, POST .../smsfund/topups            the fund
 *
 * Subscriber SMS through the AnkhOS gateway are deducted from the fund; through
 * the operator's own account they are free. Login and verification codes are
 * never charged. Amounts are integer paise; the server does all pricing.
 */

const base = (ns: string) => `/operator/${encodeURIComponent(ns)}`;

export interface SmsGatewayAccount {
  id: string;
  namespace: string;
  provider: string;
  label: string;
  sender_id: string;
  pe_id?: string;
  status: string;
  verified_at?: string;
  approved_at?: string;
  approved_by?: string;
  last_error?: string;
  enabled: boolean;
}

export interface SmsGatewaySetting {
  namespace: string;
  gateway_account_id: string | null;
  shared_fallback: boolean;
}

export interface SmsGatewayOverview {
  namespace: string;
  accounts: SmsGatewayAccount[];
  setting: SmsGatewaySetting | null;
  effective: SmsGatewaySetting | null;
  inherited: SmsGatewayAccount[];
  shared_sender: string;
  fund_grace_days: number;
  catalog?: SmsMessageType[];
}

/** One AnkhOS subscriber template. Its name links it to the operator's own copy. */
export interface SmsMessageType {
  message_type: string;
  category: string;
  text: string;
  var_count: number;
  is_code: boolean;
}

export interface SmsTemplateRow {
  message_type: string;
  category: string;
  text: string;
  var_count: number;
  is_code: boolean;
  provider_template_id: string;
  dlt_template_id: string;
  stale: boolean;
  tested_at?: string;
}

export interface SmsFund {
  namespace: string;
  seller: string;
  balancepaise: number;
  pendingpaise: number;
  pricepaise: number;
  priceincludesgst: boolean;
  gstratebp: number;
  shortsince?: string;
  hasbillingemail: boolean;
  packmessages: number;
  packnetpaise: number;
  packgstpaise: number;
  starterpaise?: number;
  entries: WalletEntry[];
}

export async function getSmsGateway(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<SmsGatewayOverview> {
  const r = await callGateway<SmsGatewayOverview>(identity, config().gatewayBase, `${base(ns)}/smsgateway`, signal ? { signal } : {});
  return r.data;
}

export async function addSmsAccount(
  identity: CallerIdentity,
  ns: string,
  input: { label: string; sender_id: string; pe_id: string; authkey: string },
): Promise<SmsGatewayAccount> {
  const r = await callGateway<SmsGatewayAccount>(identity, config().gatewayBase, `${base(ns)}/smsgateway/accounts`, {
    method: "POST",
    body: { provider: "msg91", ...input },
  });
  return r.data;
}

export async function changeSmsAccount(
  identity: CallerIdentity,
  ns: string,
  id: string,
  change: { authkey?: string; enabled?: boolean },
): Promise<SmsGatewayAccount> {
  const r = await callGateway<SmsGatewayAccount>(identity, config().gatewayBase, `${base(ns)}/smsgateway/accounts/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: change,
  });
  return r.data;
}

export async function sendSmsTest(identity: CallerIdentity, ns: string, id: string, messageType: string): Promise<SmsGatewayAccount> {
  const r = await callGateway<SmsGatewayAccount>(identity, config().gatewayBase, `${base(ns)}/smsgateway/accounts/${encodeURIComponent(id)}/verify`, {
    method: "POST",
    body: { message_type: messageType },
  });
  return r.data;
}

export async function listSmsTemplates(identity: CallerIdentity, ns: string, id: string, signal?: AbortSignal): Promise<SmsTemplateRow[]> {
  const r = await callGateway<SmsTemplateRow[] | null>(identity, config().gatewayBase, `${base(ns)}/smsgateway/accounts/${encodeURIComponent(id)}/templates`, signal ? { signal } : {});
  return r.data ?? [];
}

export async function mapSmsTemplate(identity: CallerIdentity, ns: string, id: string, messageType: string, providerTemplateId: string, dltTemplateId = ""): Promise<void> {
  await callGateway<unknown>(identity, config().gatewayBase, `${base(ns)}/smsgateway/accounts/${encodeURIComponent(id)}/templates/${encodeURIComponent(messageType)}`, {
    method: "PUT",
    body: { provider_template_id: providerTemplateId.trim(), dlt_template_id: dltTemplateId.trim() },
  });
}

export async function setSmsChoice(identity: CallerIdentity, ns: string, accountId: string, sharedFallback: boolean): Promise<SmsGatewaySetting> {
  const r = await callGateway<SmsGatewaySetting>(identity, config().gatewayBase, `${base(ns)}/smsgateway/setting`, {
    method: "PUT",
    body: { gateway_account_id: accountId, shared_fallback: sharedFallback },
  });
  return r.data;
}

/** io.ankhos: every operator's own account, unapproved first. */
export async function listSmsApprovals(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<SmsGatewayAccount[]> {
  const r = await callGateway<{ accounts: SmsGatewayAccount[] }>(identity, config().gatewayBase, `${base(ns)}/smsgateway/approvals`, signal ? { signal } : {});
  return r.data.accounts;
}

/** io.ankhos: approve an account after reading its test messages, or withdraw it. */
export async function setSmsApproval(identity: CallerIdentity, ns: string, id: string, approved: boolean): Promise<SmsGatewayAccount> {
  const r = await callGateway<SmsGatewayAccount>(identity, config().gatewayBase, `${base(ns)}/smsgateway/approvals/${encodeURIComponent(id)}`, {
    method: "POST",
    body: { approved },
  });
  return r.data;
}

export async function getSmsFund(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<SmsFund> {
  const r = await callGateway<SmsFund>(identity, config().gatewayBase, `${base(ns)}/smsfund`, signal ? { signal } : {});
  return r.data;
}

export async function recordSmsTopup(
  identity: CallerIdentity,
  ns: string,
  input: { amountpaise: number; paymentreference: string; idempotencykey: string },
): Promise<WalletEntry> {
  const r = await callGateway<WalletEntry>(identity, config().gatewayBase, `${base(ns)}/smsfund/topups`, {
    method: "POST",
    body: { ...input, paymentreference: input.paymentreference.trim() },
  });
  return r.data;
}

/** The 6-letter DLT header, as the server checks it. */
export function senderIdError(v: string): string {
  return /^[A-Za-z]{6}$/.test(v.trim()) ? "" : "Your 6-letter DLT sender ID";
}

export function authkeyError(v: string): string {
  return /^[A-Za-z0-9]{16,64}$/.test(v.trim()) ? "" : "MSG91 authkey: 16 to 64 letters and digits";
}

export function peIdError(v: string): string {
  return /^[0-9]{10,25}$/.test(v.trim()) ? "" : "DLT entity ID: 10 to 25 digits";
}

export function flowIdError(v: string): string {
  return v.trim() === "" || /^[A-Za-z0-9]{8,64}$/.test(v.trim()) ? "" : "MSG91 template ID: 8 to 64 letters and digits";
}

/** Whole rupees only (the server refuses paise). Paise, or a message. */
/** A top-up is a whole number of packs (1000 messages for Rs 350): the paise, or what is wrong. */
export function packsPaise(text: string, packPaise: number): number | string {
  const t = text.trim();
  if (!/^[1-9][0-9]{0,4}$/.test(t)) return "Enter how many packs: 1, 2, 3...";
  return Number(t) * packPaise;
}

export type FundState =
  | { kind: "ok" }
  | { kind: "grace"; stopsOn: Date | null }
  | { kind: "stopped" };

/**
 * What the balance means. Below zero SMS keep going until the grace period ends;
 * after that only login and verification codes go out. graceDays null = this
 * caller cannot read it; the date is then not shown.
 */
export function fundState(balancePaise: number, shortSince: string | undefined, graceDays: number | null, now: Date): FundState {
  if (balancePaise >= 0 || !shortSince) return { kind: "ok" };
  if (graceDays === null) return { kind: "grace", stopsOn: null };
  const stops = new Date(new Date(shortSince).getTime() + graceDays * 86_400_000);
  return stops.getTime() <= now.getTime() ? { kind: "stopped" } : { kind: "grace", stopsOn: stops };
}

/** Why a message cannot go through this own account. "" = it can. */
export function templateProblem(t: Pick<SmsTemplateRow, "provider_template_id" | "stale">): string {
  if (t.stale) return "The message changed: save its template again";
  return t.provider_template_id ? "" : "No template";
}

/** Our text with the operator's name in place of ours at the end. */
export function withSignOff(text: string, signOff: string): string {
  const name = signOff.trim().replace(/^-+\s*/, "");
  const at = text.lastIndexOf(" -");
  return name && at >= 0 ? `${text.slice(0, at)} -${name}` : text;
}

/** The same text as MSG91 holds it: ##var1##, ##var2##... */
export function msg91Text(text: string): string {
  let n = 0;
  return text.replace(/\{#var#\}/g, () => `##var${++n}##`);
}

const SHEET_COLUMNS = ["template_name", "category", "dlt_text", "msg91_text", "values", "your_dlt_template_id", "your_msg91_template_id"];

const csvCell = (v: string) => `"${v.replace(/"/g, '""')}"`;

/** The sheet an operator registers from, then fills in and uploads. */
export function templateSheet(catalog: SmsMessageType[], signOff: string): string {
  const rows = catalog.map((c) => {
    const text = withSignOff(c.text, signOff);
    return [c.message_type, c.category, text, msg91Text(text), String(c.var_count), "", ""].map(csvCell).join(",");
  });
  return [SHEET_COLUMNS.join(","), ...rows].join("\r\n") + "\r\n";
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") {
      row.push(cell.replace(/\r$/, ""));
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }
  if (cell !== "" || row.length > 0) {
    row.push(cell.replace(/\r$/, ""));
    rows.push(row);
  }
  return rows;
}

export interface SheetRow {
  message_type: string;
  provider_template_id: string;
  dlt_template_id: string;
}

/** The filled-in rows of an uploaded sheet. Rows left empty are not returned. */
export function readTemplateSheet(text: string): SheetRow[] | string {
  const [head, ...rows] = parseCsv(text.replace(/^\uFEFF/, ""));
  const col = (name: string) => (head ?? []).map((h) => h.trim()).indexOf(name);
  const name = col("template_name");
  const flow = col("your_msg91_template_id");
  const dlt = col("your_dlt_template_id");
  if (name < 0 || flow < 0 || dlt < 0) return "This is not the template sheet: download it again and fill it in";
  return rows
    .map((r) => ({
      message_type: (r[name] ?? "").trim(),
      provider_template_id: (r[flow] ?? "").trim(),
      dlt_template_id: (r[dlt] ?? "").trim(),
    }))
    .filter((r) => r.message_type !== "" && r.provider_template_id !== "");
}
